<template>
    <div class="flex flex-col items-center z-40 pointer-events-none" :style="[widthStyle, colorStyle]">
        <div class="relative drop-shadow-[0_0_0.0625rem_rgba(0,0,0,0.3)] flex flex-row pointer-events-auto">
            <!-- Left direction indicator for large phases -->
            <Tooltip v-if="props.displayDirectionIcons && props.direction === 'Left'">
                <template #trigger="tooltipProps">
                    <div
                        class="flex items-center justify-center rounded-l-xs focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 bg-(--color-strong-bg) text-(--color-text)"
                        :class="[editWidgetIconWrapperSizeClass]"
                        v-bind="tooltipProps"
                        tabindex="0"
                    >
                        <InsertLeftIcon :class="editWidgetIconSizeClass" />
                    </div>
                </template>

                <template #text>
                    <template v-if="props.isMainPhase">
                        {{ $t('change-phase-main-left') }}
                    </template>

                    <template v-else>
                        {{ $t('change-phase-before-main') }}
                    </template>
                </template>
            </Tooltip>

            <!-- Left -1 addition for 1-day main phases, direction === 'Both' is only set for 1-day main phases -->
            <div v-if="props.direction === 'Both' && props.cellSpanBefore > 0" class="flex flex-row items-center">
                <!-- Left -1 addition connection bar -->
                <div
                    class="absolute bg-(--color-strong-bg)"
                    :class="[editWidgetConnectorHeightClass]"
                    :style="[leftMinusOneConnectionBarStyle]"
                />

                <div
                    class="flex justify-center items-center gap-1 rounded-xs p-[0.0625rem] pointer-events-auto absolute bg-(--color-strong-bg) text-(--color-icon-text)"
                    :class="[editWidgetIconWrapperSizeClass]"
                    :style="[leftMinusOneStyle, iconColorStyle]"
                >
                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <button
                                v-bind="tooltipProps"
                                :aria-disabled="props.isDecreaseButtonDisabled"
                                type="button"
                                class="flex justify-center items-center text-center rounded-[0.0625rem] focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 cursor-pointer"
                                :class="[
                                    editWidgetIconInnerWrapperSizeClass,
                                    editWidgetTextSizeClass,
                                    props.isDecreaseButtonDisabled
                                        ? buttonDisabledClasses
                                        : props.isDecreasingDeleting
                                          ? deleteButtonClasses
                                          : buttonClasses,
                                ]"
                                @mouseover="send({ type: 'Focus additional left minus button' })"
                                @mouseleave="send({ type: 'Blur left button' })"
                                @focus="send({ type: 'Focus additional left minus button' })"
                                @blur="send({ type: 'Blur left button' })"
                                @click="
                                    props.cellSpanBefore === 1
                                        ? send({ type: 'Remove temporary phase left' })
                                        : send({ type: 'Click left button' })
                                "
                            >
                                {{ $t('minus-one') }}
                            </button>
                        </template>

                        <template #text>
                            <div class="w-full min-w-max text-center">
                                {{ $t('phase-reduce-length-left') }}
                            </div>
                        </template>

                        <template #header>
                            <div class="w-full min-w-max text-center">
                                {{ props.phaseName }}
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </div>

            <div
                class="flex flex-row items-center gap-[0.0625rem] p-[0.0625rem] pointer-events-auto bg-(--color-strong-bg)"
                :class="[
                    {
                        'rounded-l-xs':
                            (props.displayDirectionIcons && props.direction === 'Right') ||
                            !props.displayDirectionIcons,
                        'rounded-r-xs':
                            (props.displayDirectionIcons && props.direction === 'Left') || !props.displayDirectionIcons,
                    },
                ]"
            >
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <button
                            v-bind="tooltipProps"
                            :aria-disabled="props.isDecreaseButtonDisabled"
                            type="button"
                            class="flex items-center justify-center text-center rounded-[0.0625rem] focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 cursor-pointer"
                            :class="[
                                editWidgetIconInnerWrapperSizeClass,
                                editWidgetTextSizeClass,
                                props.isDecreaseButtonDisabled
                                    ? buttonDisabledClasses
                                    : props.isDecreasingDeleting
                                      ? deleteButtonClasses
                                      : buttonClasses,
                            ]"
                            @mouseover="send({ type: 'Focus left button' })"
                            @mouseleave="send({ type: 'Blur left button' })"
                            @focus="send({ type: 'Focus left button' })"
                            @blur="send({ type: 'Blur left button' })"
                            @click="props.isDecreaseButtonDisabled ? undefined : send({ type: 'Click left button' })"
                        >
                            <DeleteForeverIcon v-if="props.isDecreasingDeleting" :class="editWidgetIconSizeClass" />
                            <template v-else-if="props.direction === 'Both'">{{ $t('plus-one') }}</template>
                            <template v-else>{{ $t('minus-one') }}</template>
                        </button>
                    </template>

                    <template #text>
                        <div v-if="props.isDecreaseButtonDisabled" class="w-full min-w-max text-center">
                            {{ $t('phase-already-marked-for-deletion') }}
                        </div>

                        <div v-else-if="props.isDecreasingDeleting" class="w-full min-w-max text-center">
                            {{ $t('phase-mark-for-deletion') }}
                        </div>

                        <div v-else-if="props.direction === 'Both'" class="w-full min-w-max text-center">
                            {{ $t('phase-increase-length-left') }}
                        </div>

                        <div v-else class="w-full min-w-max text-center">
                            {{ $t('phase-reduce-length') }}
                        </div>
                    </template>

                    <template #header>
                        <div class="w-full text-center">{{ props.phaseName }}</div>
                    </template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <button
                            v-bind="tooltipProps"
                            type="button"
                            class="text-center rounded-[0.0625rem] focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 cursor-pointer"
                            :class="[editWidgetIconInnerWrapperSizeClass, editWidgetTextSizeClass, buttonClasses]"
                            @mouseover="send({ type: 'Focus right button' })"
                            @mouseleave="send({ type: 'Blur right button' })"
                            @focus="send({ type: 'Focus right button' })"
                            @blur="send({ type: 'Blur right button' })"
                            @click="send({ type: 'Click right button' })"
                        >
                            {{ $t('plus-one') }}
                        </button>
                    </template>

                    <template #text>
                        <div v-if="props.direction === 'Both'" class="w-full min-w-max text-center">
                            {{ $t('phase-increase-length-right') }}
                        </div>

                        <div v-else class="w-full min-w-max text-center">
                            {{ $t('phase-increase-length') }}
                        </div>
                    </template>

                    <template #header>
                        <div class="w-full min-w-max text-center">
                            {{ props.phaseName }}
                        </div>
                    </template>
                </Tooltip>
            </div>

            <!-- Right -1 addition for 1-day main phases, direction === 'Both' is only set for 1-day main phases  -->
            <div v-if="props.direction === 'Both' && props.cellSpanAfter > 0" class="flex flex-row items-center">
                <!-- Right -1 addition connection bar -->
                <div
                    class="absolute bg-(--color-strong-bg)"
                    :class="[editWidgetConnectorHeightClass]"
                    :style="[rightMinusOneConnectionBarStyle]"
                />

                <div
                    class="flex justify-center items-center gap-1 rounded-xs p-[0.0625rem] pointer-events-auto absolute bg-(--color-strong-bg) text-(--color-icon-text)"
                    :class="[editWidgetIconWrapperSizeClass]"
                    :style="[rightMinusOneStyle, iconColorStyle]"
                >
                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <button
                                v-bind="tooltipProps"
                                :aria-disabled="props.isDecreaseButtonDisabled"
                                type="button"
                                class="flex justify-center items-center text-center rounded-[0.0625rem] focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 cursor-pointer"
                                :class="[
                                    editWidgetIconInnerWrapperSizeClass,
                                    editWidgetTextSizeClass,
                                    props.isDecreaseButtonDisabled
                                        ? buttonDisabledClasses
                                        : props.isDecreasingDeleting
                                          ? deleteButtonClasses
                                          : buttonClasses,
                                ]"
                                @mouseover="send({ type: 'Focus additional right minus button' })"
                                @mouseleave="send({ type: 'Blur right button' })"
                                @focus="send({ type: 'Focus additional right minus button' })"
                                @blur="send({ type: 'Blur right button' })"
                                @click="
                                    props.cellSpanAfter === 1
                                        ? send({ type: 'Remove temporary phase right' })
                                        : send({ type: 'Click right button' })
                                "
                            >
                                {{ $t('minus-one') }}
                            </button>
                        </template>

                        <template #text>
                            <div class="w-full min-w-max text-center">
                                {{ $t('phase-reduce-length-right') }}
                            </div>
                        </template>

                        <template #header>
                            <div class="w-full min-w-max text-center">
                                {{ props.phaseName }}
                            </div>
                        </template>
                    </Tooltip>
                </div>
            </div>

            <!-- Right direction indicator for large phases -->
            <Tooltip v-if="props.displayDirectionIcons && props.direction === 'Right'">
                <template #trigger="tooltipProps">
                    <div
                        class="flex items-center justify-center rounded-r-xs pointer-events-auto focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 bg-(--color-strong-bg) text-(--color-text)"
                        :class="[editWidgetIconWrapperSizeClass]"
                        v-bind="tooltipProps"
                        :aria-labelledby="tooltipProps['aria-labelledby']"
                        tabindex="0"
                    >
                        <InsertRightIcon :class="editWidgetIconSizeClass" />
                    </div>
                </template>

                <template #text>
                    <template v-if="props.isMainPhase">
                        {{ $t('change-phase-main-right') }}
                    </template>

                    <template v-else>
                        {{ $t('change-phase-after-main') }}
                    </template>
                </template>
            </Tooltip>
        </div>

        <!-- Vertical connector bar -->
        <div
            class="bg-(--color-strong-bg)"
            :class="[editWidgetConnectorWidthClass, editWidgetConnectorExtensionsHeightClass]"
        />

        <div
            v-if="showPreviewIcon"
            class="relative flex place-content-center gap-1 rounded-xs p-0.5 drop-shadow-[0_0_0.0625rem_rgba(0,0,0,0.3)] motion-preset-pop motion-duration-300 bg-(--color-strong-bg) text-(--color-icon-text)"
            :style="[previewAreaWidthStyle, previewAreaPositionStyle, iconColorStyle]"
        >
            <InsertLeftIcon
                v-if="
                    snapshot.matches({
                        'Left direction': 'Default',
                    }) ||
                    snapshot.matches({ 'Both directions': 'Default' }) ||
                    snapshot.matches({ 'Both directions': '+1 right focused' }) ||
                    snapshot.matches({ 'Both directions': '-1 right focused' })
                "
                :class="editWidgetIconSizeClass"
            />
            <InsertLeftFilledIcon
                v-if="
                    snapshot.matches({ 'Left direction': '+1 focused' }) ||
                    snapshot.matches({ 'Both directions': '+1 left focused' })
                "
                :class="editWidgetIconSizeClass"
            />
            <InsertLeftRemovedIcon
                v-if="
                    snapshot.matches({ 'Left direction': '-1 focused' }) ||
                    snapshot.matches({ 'Both directions': '-1 left focused' })
                "
                :class="editWidgetIconSizeClass"
            />

            <DeleteForeverIcon
                v-if="
                    snapshot.matches({ 'Left direction': 'Delete focused' }) ||
                    snapshot.matches({ 'Right direction': 'Delete focused' })
                "
                :class="editWidgetIconSizeClass"
            />

            <InsertRightIcon
                v-if="
                    snapshot.matches({ 'Right direction': 'Default' }) ||
                    snapshot.matches({ 'Both directions': 'Default' }) ||
                    snapshot.matches({ 'Both directions': '+1 left focused' }) ||
                    snapshot.matches({ 'Both directions': '-1 left focused' })
                "
                :class="editWidgetIconSizeClass"
            />
            <InsertRightFilledIcon
                v-if="
                    snapshot.matches({ 'Right direction': '+1 focused' }) ||
                    snapshot.matches({ 'Both directions': '+1 right focused' })
                "
                :class="editWidgetIconSizeClass"
            />
            <InsertRightRemovedIcon
                v-if="
                    snapshot.matches({ 'Right direction': '-1 focused' }) ||
                    snapshot.matches({ 'Both directions': '-1 right focused' })
                "
                :class="editWidgetIconSizeClass"
            />
        </div>

        <div v-else :class="allocationHeightClass" />
    </div>
</template>

<script setup lang="ts">
import { useMachine } from '@xstate/vue';
import { computed, provide, ref } from 'vue';
import DeleteForeverIcon from '../../Icon/DeleteForeverIcon.vue';
import InsertLeftFilledIcon from '../../Icon/InsertLeftFilledIcon.vue';
import InsertLeftIcon from '../../Icon/InsertLeftIcon.vue';
import InsertLeftRemovedIcon from '../../Icon/InsertLeftRemovedIcon.vue';
import InsertRightFilledIcon from '../../Icon/InsertRightFilledIcon.vue';
import InsertRightIcon from '../../Icon/InsertRightIcon.vue';
import InsertRightRemovedIcon from '../../Icon/InsertRightRemovedIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../../Tooltip/injectionKeys';
import { PhaseColor, PhaseTypeDatabase } from '../Timeline.types';
import { SpecializationType } from '../allocation/SpecializationMappings';
import {
    get50ColorForPhaseType,
    getBaseColorForPhaseType,
    getPlus200ColorForPhaseType,
    getTextColorForPhaseType,
} from '../phaseColors';
import {
    allocationHeightClass,
    allocationHeightInRem,
    columnWidthInRem,
    editWidgetConnectorExtensionsHeightClass,
    editWidgetConnectorHeightClass,
    editWidgetConnectorWidthClass,
    editWidgetIconInnerWrapperSizeClass,
    editWidgetIconInnerWrapperSizeInRem,
    editWidgetIconSizeClass,
    editWidgetIconWrapperSizeClass,
    editWidgetTextSizeClass,
} from '../sharedStyles';
import { createStateMachine } from './EditPhaseWidget.state';

type EditPhaseWidgetProps = {
    phaseType: PhaseTypeDatabase;
    phaseName: string;
    phaseColor: PhaseColor;
    direction: 'Left' | 'Both' | 'Right';
    isMainPhase: boolean;
    isDecreasingDeleting: boolean;
    isDecreaseButtonDisabled: boolean;
    cellSpanBefore: number;
    cellSpanAfter: number;
    changePhaseStatus: 'Open-Popup' | 'Close-Popup' | 'Dont-Show';
    allowedSpecializations: (SpecializationType | null)[];
    hasTemporaryChangeMarker: boolean;
    displayDirectionIcons: boolean;
};

const props = defineProps<EditPhaseWidgetProps>();

const emit = defineEmits<{
    (e: 'decreasePhaseLength', direction: 'Left' | 'Right'): void;
    (e: 'increasePhaseLength', direction: 'Left' | 'Right'): void;
    (e: 'toggle-change-phase-type'): void;
    (e: 'change-specialization', specialization: SpecializationType | null): void;
}>();

const stateMachine = createStateMachine({
    direction: props.direction,
    isDecreasingDeleting: props.isDecreasingDeleting,
    onDeletePhase: () => {
        if (props.direction === 'Both') {
            throw new Error('onDeletePhase should never be called for direction "Both"');
        }
        triggerHideTooltip();
        emit('decreasePhaseLength', props.direction);
    },
    onDecreasePhaseLength: (direction) => {
        triggerHideTooltip();
        emit('decreasePhaseLength', direction);
    },
    onIncreasePhaseLength: (direction) => {
        triggerHideTooltip();
        emit('increasePhaseLength', direction);
    },
});

const { snapshot, send } = useMachine(stateMachine);

const colorStyle = computed(() => ({
    '--color-strong-bg': `var(${getBaseColorForPhaseType(props.phaseColor)})`,
    '--color-text': `var(${getTextColorForPhaseType(props.phaseColor)})`,
    '--color-light-bg': `var(${get50ColorForPhaseType(props.phaseColor)})`,
    '--color-active-bg': `var(${getPlus200ColorForPhaseType(props.phaseColor)})`,
}));

const buttonClasses =
    'text-gray-700 active:text-gray-50 hover:text-(--color-text) bg-(--color-light-bg) hover:bg-(--color-strong-bg) active:bg-(--color-active-bg)';

const buttonDisabledClasses = 'bg-gray-100 text-gray-500 cursor-not-allowed';

const deleteButtonClasses = computed(() => {
    return `
        bg-red-700 text-red-50
        hover:bg-red-800 hover:text-red-50
        active:bg-red-900 active:text-red-50
        focus-visible:outline-red-700
    `;
});

const iconColorStyle = computed(() => {
    return {
        '--color-icon-text': `var(${getTextColorForPhaseType(props.phaseColor)})`,
    };
});

// This is used to force close the Tooltip after clicking any button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

const showPreviewIcon = computed(() => {
    return (
        snapshot.value.matches({ 'Left direction': '+1 focused' }) ||
        snapshot.value.matches({ 'Both directions': '+1 left focused' }) ||
        snapshot.value.matches({ 'Left direction': '-1 focused' }) ||
        snapshot.value.matches({ 'Both directions': '-1 left focused' }) ||
        snapshot.value.matches({ 'Left direction': 'Delete focused' }) ||
        snapshot.value.matches({ 'Right direction': 'Delete focused' }) ||
        snapshot.value.matches({ 'Right direction': '+1 focused' }) ||
        snapshot.value.matches({ 'Both directions': '+1 right focused' }) ||
        snapshot.value.matches({ 'Right direction': '-1 focused' }) ||
        snapshot.value.matches({ 'Both directions': '-1 right focused' })
    );
});

const widthStyle = computed(() => {
    if (props.displayDirectionIcons) {
        return {
            width: `${columnWidthInRem + 1.125}rem`,
        };
    }

    return { width: `${columnWidthInRem}rem` };
});

const distanceBetweenColumnEdgeAndMinusOneButton = computed(() => {
    const widthButtonInRem = 1.125;

    return (columnWidthInRem - widthButtonInRem) / 2;
});

const leftMinusOneConnectionBarStyle = computed(() => {
    return {
        left: `-${distanceBetweenColumnEdgeAndMinusOneButton.value}rem`,
        width: `${distanceBetweenColumnEdgeAndMinusOneButton.value}rem`,
    };
});

const leftMinusOneStyle = computed(() => {
    const widthButtonInRem = 1.125;

    return {
        left: `-${distanceBetweenColumnEdgeAndMinusOneButton.value + widthButtonInRem}rem`,
    };
});

const rightMinusOneConnectionBarStyle = computed(() => {
    return {
        right: `-${distanceBetweenColumnEdgeAndMinusOneButton.value}rem`,
        width: `${distanceBetweenColumnEdgeAndMinusOneButton.value}rem`,
    };
});

const rightMinusOneStyle = computed(() => {
    const widthButtonInRem = 1.125;

    return {
        right: `-${distanceBetweenColumnEdgeAndMinusOneButton.value + widthButtonInRem}rem`,
    };
});

const previewAreaWidthStyle = computed(() => {
    if (props.direction === 'Both') {
        return { width: `${editWidgetIconInnerWrapperSizeInRem * 2 + 0.0625}rem` };
    }

    return { width: `${editWidgetIconInnerWrapperSizeInRem}rem` };
});

const previewAreaPositionStyle = computed(() => {
    const differenceIconAllocationHeightInRem = allocationHeightInRem - editWidgetIconInnerWrapperSizeInRem;

    return {
        marginBottom: `${differenceIconAllocationHeightInRem / 2}rem`,
        marginTop: `${differenceIconAllocationHeightInRem / 2}rem`,
    };
});
</script>

<style scoped>
.connect-bar-inverted-border-radius {
    position: relative;
}

.connect-bar-inverted-border-radius::after {
    content: '';
    position: absolute;
    height: 4px;
    bottom: -5px;
    left: 50%;
    transform: translate(-50%);
}
</style>
